import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { HeaderContainer } from "./viewreport";
import { PixelStyle } from "../../components/layout";
import { Trash2 } from "lucide-react";
import { SearchableDropdown } from "../../components/reportComponents";
import { GetTourByIdAPI, UpdateTourAPI } from "../../apis/tour";
import { useGeolocated } from "react-geolocated";
import { fetchAllCompanyList } from "../../apis/company/companyList";
import QRCode from "react-qr-code";
import { generateQrcodePDF } from "../../components/functions/generateQrcodePDF";
import { BsCopy, BsPrinter } from "react-icons/bs";
import { generateUniqueId } from "../../components/generateUniqueId";
import {
  copyQRCodeToClipboard,
  HandleCopyToClipboard,
} from "../../utils/CopyToClipboard";
import { AlertModal } from "../../components/Modal";

const EditTour = () => {
  const { tourId } = useParams();
  const [tourName, setTourName] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyLocation, setCompanyLocation] = useState("");
  const [companyLocationId, setCompanyLocationId] = useState("");
  const [checkpoints, setCheckpoints] = useState([]);
  const [error, setError] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [distance, setDistance] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info");

  const { coords, isGeolocationAvailable, isGeolocationEnabled } =
    useGeolocated({
      positionOptions: { enableHighAccuracy: true },
      userDecisionTimeout: 5000,
    });

  // Fetch tour and company data
  const fetchData = useCallback(async () => {
    setFetchLoading(true);
    setError(null);
    try {
      // Fetch tour data
      const tourResponse = await GetTourByIdAPI(tourId);
      if (tourResponse && tourResponse.tourName) {
        setTourName(tourResponse.tourName);
        setCompanyId(tourResponse.companyId || "");
        setCompanyName(tourResponse.companyName || "");
        setCompanyLocation(tourResponse.companyLocation || "");
        setDistance(tourResponse?.distance ? parseFloat(tourResponse.distance) : 0);
        setCompanyLocationId(tourResponse.companyLocationId || "");
        // Sort checkpoints by sequence number to ensure correct order
        const sortedCheckpoints = tourResponse.checkpoints?.length > 0
          ? [...tourResponse.checkpoints].sort((a, b) => {
              const seqA = parseInt(a.sequence) || 0;
              const seqB = parseInt(b.sequence) || 0;
              return seqA - seqB;
            })
          : [];

        const tour_response_checkpoints = tourResponse?.checkpoints
        setCheckpoints(
          tour_response_checkpoints.length > 0
            ? tour_response_checkpoints.map((cp) => {
                const [lat, lon] = cp.location
                  ? cp.location.split(",")
                  : ["", ""];
                // Use the actual sequence from API, not array index
                const sequenceNum = parseInt(cp.sequence) || 1;
                return {
                  id: cp.id || generateUniqueId(),
                  name: cp.checkpointName || "",
                  lat: lat || "",
                  lon: lon || "",
                  location: cp.location || "",
                  qrcode: cp.qrcode || "",
                  // sequence: sequenceNum,
                  sequence: cp?.sequence || "",
                };
              })
            : [
                {
                  id: generateUniqueId(),
                  name: "",
                  lat: "",
                  lon: "",
                  location: "",
                  qrcode: "",
                  sequence: 1,
                },
              ]
        );
      } else {
        throw new Error("Invalid tour data received");
      }

      // Fetch company list
      const companyData = await fetchAllCompanyList();
      if (Array.isArray(companyData)) {
        setCompanyList(companyData);
        const isMatch = companyData.find(
          (i) => i?.id == tourResponse?.companyId
        );
        if (isMatch) {
          // Set companyName from the matched company to ensure dropdown works correctly
          setCompanyName(isMatch.company || tourResponse.companyName || "");
          setCompanyLocation(
            `${isMatch.address || ""}, ${isMatch.city || ""}, ${isMatch?.state || ""} ${isMatch.zipCode || ""}`.trim()
          );
        }
      } else {
        throw new Error("Invalid company list data");
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to load tour or company data. Please try again.";
      setError(errorMessage);
      console.error("Error fetching data:", error);
    } finally {
      setFetchLoading(false);
    }
  }, [tourId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Add a new checkpoint
  const addCheckpoint = () => {
    // Find the highest sequence number and add 1
    const maxSequence = checkpoints.length > 0
      ? Math.max(...checkpoints.map(cp => cp.sequence || 0))
      : 0;
    
    setCheckpoints([
      ...checkpoints,
      {
        id: generateUniqueId(),
        name: "",
        lat: "",
        lon: "",
        location: "",
        qrcode: "",
        sequence: maxSequence + 1,
        distance: distance || 0,
      },
    ]);
  };

  // Update a checkpoint
  const updateCheckpoint = (id, updates) => {
    setCheckpoints(
      checkpoints.map((cp) => (cp.id === id ? { ...cp, ...updates } : cp))
    );
  };

  // Remove a checkpoint
  const removeCheckpoint = (id) => {
    if (checkpoints.length === 1) {
      setAlertMessage("At least one checkpoint is required");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    // Remove checkpoint and renumber sequences starting from 1
    const updatedCheckpoints = checkpoints
      .filter((cp) => cp.id !== id)
      .map((cp, index) => ({ ...cp, sequence: index + 1 }));
    setCheckpoints(updatedCheckpoints);
  };

  // Fetch geolocation
  const fetchLocation = (checkpointId) => {
    if (!isGeolocationAvailable) {
      setAlertMessage("Geolocation is not supported by your browser.");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (!isGeolocationEnabled) {
      setAlertMessage("Please enable geolocation permissions in your browser.");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (coords) {
      const lat = coords.latitude.toString();
      const lon = coords.longitude.toString();
      const location = `${lat},${lon}`;
      const qrcode = location; // Match CreateTour
      updateCheckpoint(checkpointId, { lat, lon, location, qrcode });
      setAlertMessage("Location fetched successfully!");
      setAlertType("success");
      setShowAlert(true);
    } else {
      setAlertMessage("Unable to fetch location. Please try again or enter manually.");
      setAlertType("error");
      setShowAlert(true);
    }
  };

  // Generate QR code
  const generateQrCode = (checkpointId) => {
    const checkpoint = checkpoints.find((cp) => cp.id === checkpointId);
    if (!checkpoint.lat || !checkpoint.lon) {
      setAlertMessage("Both latitude and longitude are required for QR code");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    const location = `${checkpoint.lat},${checkpoint.lon}`;
    updateCheckpoint(checkpointId, { location, qrcode: location });
    setAlertMessage("QR code generated successfully!");
    setAlertType("success");
    setShowAlert(true);
  };

  // Clear checkpoint coordinates
  const clearCheckpoint = (checkpointId) => {
    updateCheckpoint(checkpointId, {
      lat: "",
      lon: "",
      location: "",
      qrcode: "",
    });
  };

  // Handle tour update
  const updateTourHandler = async () => {
    // Validation
    if (!tourName?.trim()) {
      setAlertMessage("Please enter a tour name");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (!companyId || !companyName) {
      setAlertMessage("Please select a company");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    // Note: companyLocationId is set but may not be required by API
    if (checkpoints.length === 0) {
      setAlertMessage("At least one checkpoint is required");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    if (checkpoints.some((cp) => !cp.name?.trim() || !cp.location?.trim())) {
      setAlertMessage("All checkpoints must have a name and location");
      setAlertType("error");
      setShowAlert(true);
      return;
    }
    
    // Validate coordinates format
    const invalidCheckpoints = checkpoints.filter(
      (cp) => cp.location && !/^-?\d+\.?\d*,-?\d+\.?\d*$/.test(cp.location)
    );
    if (invalidCheckpoints.length > 0) {
      setAlertMessage("Invalid location format. Please use latitude,longitude format");
      setAlertType("error");
      setShowAlert(true);
      return;
    }

    const tourData = {
      updateTourId: tourId,
      tourName: tourName.trim(),
      companyId,
      companyName,
      status: "active",
      distance: distance?.toString() || "0",
      // Sort checkpoints by sequence before sending to API
      checkpoints: [...checkpoints]
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .map((cp, index) => ({
          id: cp.id,
          checkpointName: cp.name.trim(),
          location: cp.location.trim(),
          qrcode: cp.qrcode || cp.location.trim(),
          // Ensure sequence starts from 1 and is sequential
          sequence: (index + 1).toString(),
        })),
    };

    setUpdateLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await UpdateTourAPI(tourData);
      setAlertMessage("Tour updated successfully!");
      setAlertType("success");
      setShowAlert(true);

      fetchData();
    } catch (error) {
      const errorMessage = error?.message || "Failed to update tour. Please try again.";
      setAlertMessage(errorMessage);
      setAlertType("error");
      setShowAlert(true);
      console.error("Error updating tour:", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCopy = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setAlertMessage("Copied to clipboard!");
      setAlertType("success");
      setShowAlert(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
      setAlertMessage("Failed to copy to clipboard");
      setAlertType("error");
      setShowAlert(true);
    }
  };

  const qrRef = useRef();

  let test = []

  return (
    <div
      className={`w-full text-sm h-full text-nowrap ${PixelStyle}`}
      role="main"
    >
      <HeaderContainer pageTitle="Edit Tour" isRedirecting={false}>
        <span className="text-black">Edit Tour</span>
      </HeaderContainer>

      <div className="p-6 space-y-4">
        {error && (
          <div
            className="bg-red-100 text-red-700 p-3 rounded"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </div>
        )}
        {successMessage && (
          <div
            className="bg-green-100 text-green-700 p-3 rounded"
            role="alert"
            aria-live="assertive"
          >
            {successMessage}
          </div>
        )}
        {fetchLoading && (
          <div className="bg-blue-100 text-blue-700 p-3 rounded">
            Loading tour data...
          </div>
        )}
        <div className="w-full flex items-center justify-between gap-4 bg-white shadow p-3 rounded-lg">
          <div className="flex items-center gap-3 w-full">
            <input
              placeholder="Tour Name"
              className="border p-2 rounded w-full"
              value={tourName}
              onChange={(e) => setTourName(e.target.value)}
              aria-label="Tour name"
              required
            />
            <SearchableDropdown
              users={companyList}
              selectedCompany={companyName}
              setCompanyId={setCompanyId}
              onSelect={setCompanyName}
              setCompanyLocationId={setCompanyLocationId}
              setCompanyLocation={setCompanyLocation}
            />
            <div className="border border-zinc-200 p-2 rounded-md w-full text-nowrap overflow-x-auto min-w-40">
              {companyLocation ? (
                companyLocation
              ) : (
                <span className="text-zinc-400">Company Location</span>
              )}
            </div>

            <input
              type="number"
              min="0"
              step="0.1"
              placeholder="Distance (km)"
              className="border p-2 rounded w-full"
              value={distance}
              onChange={(e) => {
                const value = e.target.value;
                setDistance(value === "" ? 0 : parseFloat(value) || 0);
              }}
              aria-label="Tour distance in kilometers"
            />

            <button
              onClick={addCheckpoint}
              className="border px-4 py-2 rounded text-sm bg-white hover:bg-gray-100"
              aria-label="Add new checkpoint"
            >
              + Add Checkpoint
            </button>
            <button
              onClick={updateTourHandler}
              disabled={updateLoading || fetchLoading}
              className={`bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                updateLoading || fetchLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-blue-700"
              }`}
              aria-label="Update tour"
            >
              {updateLoading ? "Updating..." : "Update Tour"}
            </button>
          </div>
        </div>

        <div className="w-full bg-white shadow p-3 rounded-lg">
          <table className="w-full border" role="grid">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2" scope="col">
                  Sequence
                </th>
                <th className="border px-4 py-2" scope="col">
                  Checkpoint Name
                </th>
                <th className="border px-4 py-2" scope="col">
                  Location
                </th>
                <th className="border px-4 py-2" scope="col">
                  QR Code
                </th>
                <th className="border px-4 py-2" scope="col">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {checkpoints.map((cp, idx) => {
                const hasCoordinates = cp.lat && cp.lon;
                
                test.push(cp.sequence);

                return (
                   <tr key={cp.id}>
                    <td className="border px-4 py-2 text-center">
                      {cp.sequence}
                    </td>
                    <td className="border px-4 py-2">
                      <input
                        value={cp?.name}
                        onChange={(e) =>
                          updateCheckpoint(cp.id, { name: e.target.value })
                        }
                        className="w-full border rounded p-1"
                        placeholder="Checkpoint Name"
                        aria-label={`Checkpoint name for sequence ${cp.sequence}`}
                      />
                    </td>
                    <td className="border px-4 py-2">
                      <span className="flex items-center gap-2">
                        <input
                          type="number"
                          step="any"
                          placeholder="Latitude"
                          value={cp.lat}
                          onChange={(e) => {
                            const lat = e.target.value;
                            const location =
                              lat && cp.lon ? `${lat},${cp.lon}` : "";
                            updateCheckpoint(cp.id, { lat, location });
                          }}
                          className="w-28 border rounded p-1"
                          aria-label={`Latitude for checkpoint ${cp.sequence}`}
                        />
                        <input
                          type="number"
                          step="any"
                          placeholder="Longitude"
                          value={cp.lon}
                          onChange={(e) => {
                            const lon = e.target.value;
                            const location =
                              cp.lat && lon ? `${cp.lat},${lon}` : "";
                            updateCheckpoint(cp.id, { lon, location });
                          }}
                          className="w-28 border rounded p-1"
                          aria-label={`Longitude for checkpoint ${cp.sequence}`}
                        />
                        {!hasCoordinates && (
                          <button
                            onClick={() => fetchLocation(cp.id)}
                            className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                            aria-label={`Fetch location for checkpoint ${cp.sequence}`}
                          >
                            Fetch
                          </button>
                        )}
                        {hasCoordinates && (
                          <button
                            onClick={() => generateQrCode(cp.id)}
                            className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                            aria-label={`Generate QR code for checkpoint ${cp.sequence}`}
                          >
                            Generate
                          </button>
                        )}
                        <button
                          onClick={() => clearCheckpoint(cp.id)}
                          className="bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                          aria-label={`Clear coordinates for checkpoint ${cp.sequence}`}
                        >
                          Clear
                        </button>
                      </span>
                    </td>
                    <td className="border px-4 py-2">
                      <div ref={qrRef}>
                        {cp.qrcode ? (
                          <QRCode
                            size={50}
                            style={{
                              height: "auto",
                              maxWidth: "100%",
                              width: "100%",
                            }}
                            value={cp?.qrcode}
                            viewBox={`0 0 256 256`}
                          />
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </td>
                    <td className="border px-4 py-2">
                      <div className="flex items-center justify-center gap-2">
                        {cp.qrcode && (
                          <>
                            <button
                              onClick={(e) =>
                                copyQRCodeToClipboard(
                                  e.currentTarget
                                    .closest("tr")
                                    .querySelector("svg"),
                                  (message) => {
                                    setAlertMessage(message);
                                    setAlertType("success");
                                    setShowAlert(true);
                                  },
                                  (error) => {
                                    setAlertMessage(error);
                                    setAlertType("error");
                                    setShowAlert(true);
                                  }
                                )
                              }
                              className="text-blue-500  hover:bg-blue-100 p-1.5 rounded-md"
                              aria-label={`Copy QR code for checkpoint ${cp?.sequence}`}
                            >
                              <BsCopy size={18} />
                            </button>
                            <button
                              onClick={async () => {
                                const success = await generateQrcodePDF(cp?.qrcode, cp?.name, (error) => {
                                  setAlertMessage(error);
                                  setAlertType("error");
                                  setShowAlert(true);
                                });
                                if (success) {
                                  setAlertMessage("PDF generated successfully!");
                                  setAlertType("success");
                                  setShowAlert(true);
                                }
                              }}
                              className="text-blue-500  hover:bg-blue-100 p-1.5 rounded-md"
                              aria-label={`Generate QR code PDF for checkpoint ${cp?.sequence}`}
                            >
                              <BsPrinter size={18} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => removeCheckpoint(cp?.id)}
                          aria-label={`Delete checkpoint ${cp?.sequence}`}
                          className="text-red-500  hover:bg-red-100 p-1.5 rounded-md "
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        title="Tour Management"
        message={alertMessage}
        type={alertType}
        buttonText="OK"
      />
    </div>
  );
};

export default EditTour;
